/* eslint-disable @typescript-eslint/no-explicit-any */
import { UserManager, WebStorageStateStore } from 'oidc-client';
import { OidcConfig } from '../../types/oidc-config';
import { OidcMetadataConfig } from '../../types/oidc-metadata-config';
import { OIDC_METADATA_CONFIG } from '../../config/oidc-metadata-config';
import Claims from '../../types/claims';

export abstract class BaseOidcService {
    protected oidcConfig: OidcConfig | undefined;
    protected oidcMetadataConfig: OidcMetadataConfig | undefined;
    protected userManager: UserManager | undefined;
    protected logoutHandler?: () => void;

    constructor(oidcConfig?: OidcConfig) {
        if(oidcConfig) {
            this.oidcConfig = oidcConfig;
            this.oidcMetadataConfig = this.createOidcMetadataConfig(oidcConfig.authority);
            this.userManager = this.initUserManager(this.oidcConfig, this.oidcMetadataConfig);
        }
    }

    protected fetchUserInfo = async (userInfoUrl: string, accessToken: string): Promise<Claims> => {
        const response = await fetch(userInfoUrl, {
            method: "GET",
            headers: {
                Authorization: 'Bearer ' + accessToken
            }
        });

        if(response.ok) {
            const responseJson = await response.json();
            return this.mapClaims(responseJson);
        } else {
            return Promise.reject({
                message: "Unable to fetch user info",
                status: response.status
            })
        }
    };

    protected mapClaims = (userInfo: any) : Claims => {
        return {
            id: Number(userInfo.rw_cuid),
            firstName: userInfo.given_name,
            lastName: userInfo.family_name,
            userClass:userInfo.rw_usrcls,
            locationId: userInfo.rw_lid,
            masterAccountId: userInfo.rw_maid,
            availableLocations: userInfo.rw_usrlocs ? userInfo.rw_usrlocs.split(' ') : [],
            impersonatorId: userInfo.rw_impersonator_id,
            permissions: userInfo.rw_prms
              ? userInfo.rw_prms.split(' ').map(Number).filter((v: number) => !isNaN(v))
              : [],

        }
    }

    private initUserManager = (
        oidcConfig: OidcConfig, oidcMetadataConfig: OidcMetadataConfig): UserManager => {
        return new UserManager({
            clockSkew: 900,
            monitorSession: false,
            userStore: new WebStorageStateStore({ store: window.localStorage }),
            metadata: oidcMetadataConfig,
            ...oidcConfig,
        });
    };

    private createOidcMetadataConfig(oidcAuthority: string): OidcMetadataConfig {
        return this.replaceFieldValueInConfig(
            OIDC_METADATA_CONFIG, '{oidc_authority}', oidcAuthority);
    }

    private replaceFieldValueInConfig(metadataConfig: OidcMetadataConfig,
        fieldName: string, fieldValue: string): OidcMetadataConfig {
        const entries = Object.entries(metadataConfig);

        for (const entry of entries) {
            if (typeof entry[1] === 'string') {
                entry[1] = entry[1].replace(fieldName, fieldValue);
            }
        }

        return Object.assign({}, ...Array.from(entries, ([k, v]) => ({ [k]: v })));
    }
}
